import { useAuthStore } from '../store/authStore';
import { useFamilyStore } from '../store/familyStore';
import { decodeJWT } from '../utils/jwt';

export interface CurrentUser {
  avatar: string | undefined;
  initials: string;
  color: string;
  displayName: string;
}

/**
 * Returns the current logged-in user's avatar, initials, color, and display name.
 * When in Family device mode, returns family avatar/name instead.
 * Otherwise: JWT claims (picture, name) then family member matching JWT sub,
 * then owner, then first member. Safe when no family is loaded yet.
 */
export function useCurrentUser(): CurrentUser {
  const { idToken, isFamilyMode } = useAuthStore();
  const { family } = useFamilyStore();

  if (isFamilyMode && family) {
    return {
      avatar: family.avatar,
      initials: family.name?.charAt(0).toUpperCase() || 'F',
      color: '#3b82f6',
      displayName: family.name || 'Family',
    };
  }

  const getCurrentUserId = (): string | null => {
    if (!idToken) return null;
    try {
      const decoded = decodeJWT(idToken);
      return decoded?.sub || null;
    } catch {
      return null;
    }
  };

  const currentUserId = getCurrentUserId();
  const currentUserMember = currentUserId
    ? family?.members.find((member) => member.userId === currentUserId)
    : null;

  const decoded = idToken ? decodeJWT(idToken) : null;
  const authName: string | undefined =
    decoded?.name || decoded?.email || decoded?.preferred_username;
  const authAvatar: string | undefined = decoded?.picture;

  const fallbackMember =
    currentUserMember ||
    family?.members.find((member) => member.role === 'owner') ||
    family?.members?.[0];

  const displayName =
    authName || currentUserMember?.name || fallbackMember?.name || 'User';
  const initials = displayName.charAt(0).toUpperCase() || 'U';
  const color = currentUserMember?.color || fallbackMember?.color || '#3b82f6';
  const avatar =
    authAvatar || currentUserMember?.avatar || fallbackMember?.avatar;

  return { avatar, initials, color, displayName };
}
